import React from 'react';
import type { ToolCallGenericMessage } from '@terra/shared-dto-chat-types';
import type { MessageListCallbacks } from '../components/ChatMessageList';
import type { RenderablePart } from '../components/chat-message-list-grouping';
import {
  getPendingSingleActionHITLPartIds,
  getPendingSingleActionHITLPartIdsFromItems,
} from '../components/chat-message-list-helpers';
import {
  mergeSiblingHITLActionEvents,
  type HITLActionMessageEvent,
} from '../components/HITL/hitl-decision.utils';
import type { ProcessedMessage } from '../utils/message-processing';

type DeferredSubmission = {
  resolve: () => void;
  reject: (error: Error) => void;
};

type SiblingHITLGroupState = {
  eventsByPartId: Record<string, HITLActionMessageEvent>;
  deferredSubmissions: DeferredSubmission[];
};

type SiblingHITLGroups = Record<string, SiblingHITLGroupState>;

export type BuildCallbacksForItems = (args: {
  groupKey: string;
  items: RenderablePart[];
}) => MessageListCallbacks;

const SIBLING_GROUP_RESET_ERROR_MESSAGE =
  'Sibling HITL decisions were reset before submission';
const SIBLING_GROUP_NOT_SUBMITTED_ERROR_MESSAGE =
  'Sibling HITL decisions were not submitted';

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

const rejectDeferredSubmissions = ({
  deferredSubmissions,
  error,
}: {
  deferredSubmissions: DeferredSubmission[];
  error: Error;
}) => {
  deferredSubmissions.forEach((deferred) => deferred.reject(error));
};

const settleDeferredSubmissions = ({
  deferredSubmissions,
  submission,
}: {
  deferredSubmissions: DeferredSubmission[];
  submission: void | Promise<void>;
}) => {
  if (!submission) {
    rejectDeferredSubmissions({
      deferredSubmissions,
      error: new Error(SIBLING_GROUP_NOT_SUBMITTED_ERROR_MESSAGE),
    });

    return;
  }

  submission.then(
    () => deferredSubmissions.forEach((deferred) => deferred.resolve()),
    (error: unknown) =>
      rejectDeferredSubmissions({ deferredSubmissions, error: toError(error) }),
  );
};

const buildProcessedMessagesIdentity = (
  processedMessages: ProcessedMessage[],
): string =>
  processedMessages
    .map(
      (processedMessage) =>
        `${processedMessage.message.id ?? processedMessage.index}:${processedMessage.message.parts.length}`,
    )
    .join('|');

export function useSiblingHITLCallbacks({
  callbacks,
  processedMessages,
  toolResultsById,
}: {
  callbacks: MessageListCallbacks;
  processedMessages: ProcessedMessage[];
  toolResultsById: Record<string, ToolCallGenericMessage>;
}) {
  const groupsRef = React.useRef<SiblingHITLGroups>({});
  const processedMessagesIdentity = React.useMemo(
    () => buildProcessedMessagesIdentity(processedMessages),
    [processedMessages],
  );

  React.useEffect(
    () => () => {
      const groups = groupsRef.current;

      groupsRef.current = {};
      Object.values(groups).forEach((group) =>
        rejectDeferredSubmissions({
          deferredSubmissions: group.deferredSubmissions,
          error: new Error(SIBLING_GROUP_RESET_ERROR_MESSAGE),
        }),
      );
    },
    [processedMessagesIdentity],
  );

  const buildCallbacksForPendingHITLPartIds = React.useCallback(
    ({
      groupKey,
      pendingHITLPartIds,
    }: {
      groupKey: string;
      pendingHITLPartIds: string[];
    }): MessageListCallbacks => {
      const submitHITLActionEvent = callbacks.onHITLActionMessageEvent;

      if (pendingHITLPartIds.length <= 1 || !submitHITLActionEvent) {
        return callbacks;
      }

      return {
        ...callbacks,
        onHITLActionMessageEvent: (event) => {
          const hitlPartId = event.dataMessage.id;

          if (!hitlPartId || !pendingHITLPartIds.includes(hitlPartId)) {
            return submitHITLActionEvent(event);
          }

          const group = groupsRef.current[groupKey];
          const eventsByPartId = {
            ...(group?.eventsByPartId ?? {}),
            [hitlPartId]: event,
          };
          const deferredSubmissions = group?.deferredSubmissions ?? [];
          const isGroupComplete = pendingHITLPartIds.every(
            (id) => eventsByPartId[id],
          );

          if (!isGroupComplete) {
            return new Promise<void>((resolve, reject) => {
              groupsRef.current[groupKey] = {
                eventsByPartId,
                deferredSubmissions: [
                  ...deferredSubmissions,
                  { resolve, reject },
                ],
              };
            });
          }

          delete groupsRef.current[groupKey];
          const mergedEvent = mergeSiblingHITLActionEvents({
            pendingHITLPartIds,
            eventsByPartId,
          });
          const submission = mergedEvent
            ? submitHITLActionEvent(mergedEvent)
            : undefined;

          settleDeferredSubmissions({ deferredSubmissions, submission });

          return submission;
        },
      };
    },
    [callbacks],
  );

  const buildCallbacksForMessage = React.useCallback(
    (processedMessage: ProcessedMessage): MessageListCallbacks =>
      buildCallbacksForPendingHITLPartIds({
        groupKey:
          processedMessage.message.id ?? `message-${processedMessage.index}`,
        pendingHITLPartIds: getPendingSingleActionHITLPartIds({
          processedMessage,
          toolResultsById,
        }),
      }),
    [buildCallbacksForPendingHITLPartIds, toolResultsById],
  );

  const buildCallbacksForItems = React.useCallback<BuildCallbacksForItems>(
    ({ groupKey, items }) =>
      buildCallbacksForPendingHITLPartIds({
        groupKey,
        pendingHITLPartIds: getPendingSingleActionHITLPartIdsFromItems({
          items,
          toolResultsById,
        }),
      }),
    [buildCallbacksForPendingHITLPartIds, toolResultsById],
  );

  return { buildCallbacksForMessage, buildCallbacksForItems };
}
